import type {
  CampaignCreationCapabilities,
  SmartCreationTargetingStrategyKind,
} from "@adpilot/shared";
import { IconPlus } from "@tabler/icons-react";
import type { RefObject } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from "@/components/ui/input-group";
import { SegmentControl, SegmentControlItem } from "@/components/ui/segment-control";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { allowedMatchTypes, type EntryMatchType, type EntryType } from "./strategy-target-model";
import type { TargetEntryFormState } from "./use-target-entry-form";

interface TargetEntryFormProps {
  form: TargetEntryFormState;
  entryValuesRef: RefObject<HTMLTextAreaElement>;
  branchKind: SmartCreationTargetingStrategyKind;
  categorySupported: boolean;
  capabilities: CampaignCreationCapabilities;
  mutationPending: boolean;
  initialEntryMatchType: EntryMatchType;
  initialEntryBid: string;
  /** Staged target operations still count toward dirtiness while the entry form changes. */
  stagedOperationCount: number;
  onDirtyChange: (dirty: boolean) => void;
  onSubmit: () => void;
}

export function TargetEntryForm({
  form,
  entryValuesRef,
  branchKind,
  categorySupported,
  capabilities,
  mutationPending,
  initialEntryMatchType,
  initialEntryBid,
  stagedOperationCount,
  onDirtyChange,
  onSubmit,
}: TargetEntryFormProps) {
  const { t } = useTranslation("smartCreation");
  const {
    entryType,
    setEntryType,
    entryValues,
    setEntryValues,
    entryMatchType,
    setEntryMatchType,
    entryBid,
    setEntryBid,
    entryError,
    setEntryError,
    categorySearch,
    selectedCategory,
    setSelectedCategory,
  } = form;
  return (
    <FieldGroup>
      <div>
        <h3 id="smart-creation-target-inspector-title" className="font-semibold text-ink">
          {t("strategy.editor.enterTitle")}
        </h3>
        <p className="mt-1 text-xs leading-5 text-muted-foreground">
          {t("strategy.editor.enterDescription")}
        </p>
      </div>
      <SegmentControl<EntryType>
        disabled={mutationPending}
        aria-label={t("strategy.editor.enterTitle")}
        value={entryType}
        onValueChange={(value) => {
          setEntryType(value);
          setEntryError(null);
        }}
        widthMode="equal"
      >
        <SegmentControlItem value="keyword">{t("strategy.editor.keyword")}</SegmentControlItem>
        <SegmentControlItem value="product">{t("strategy.editor.product")}</SegmentControlItem>
        {branchKind === "research" && categorySupported ? (
          <SegmentControlItem value="category">{t("strategy.editor.category")}</SegmentControlItem>
        ) : null}
      </SegmentControl>
      {entryType === "category" ? (
        <Field>
          <FieldLabel htmlFor="smart-creation-target-category-search">
            {t("strategy.editor.categorySearchLabel")}
          </FieldLabel>
          <Input
            id="smart-creation-target-category-search"
            data-target-category-query
            value={categorySearch.query}
            onChange={(event) => categorySearch.setQuery(event.currentTarget.value)}
            placeholder={t("strategy.editor.categorySearchPlaceholder")}
            autoComplete="off"
          />
          {categorySearch.query.trim().length < 2 ? (
            <FieldDescription>{t("strategy.editor.categorySearchHint")}</FieldDescription>
          ) : null}
          {categorySearch.loading ? (
            <p role="status" className="text-xs text-muted-foreground">
              {t("strategy.editor.categorySearchLoading")}
            </p>
          ) : null}
          {categorySearch.failed ? (
            <p data-target-category-error className="text-xs text-destructive">
              {t("strategy.editor.categorySearchFailed")}
            </p>
          ) : null}
          {selectedCategory ? (
            <p className="text-xs text-muted-foreground">
              {selectedCategory.path} · {selectedCategory.id}
            </p>
          ) : null}
          {categorySearch.categories.length > 0 ? (
            <div
              role="listbox"
              aria-label={t("strategy.editor.categorySearchResults")}
              className="max-h-48 overflow-y-auto rounded-md border border-line bg-surface p-1"
            >
              {categorySearch.categories.map((category) => (
                <button
                  key={category.id}
                  type="button"
                  role="option"
                  aria-selected={entryValues.split(/[\n,]/).some((id) => id.trim() === category.id)}
                  data-target-category-option={category.id}
                  className="flex w-full flex-col items-start rounded-sm px-3 py-2 text-left text-sm hover:bg-subtle focus-visible:bg-subtle focus-visible:outline-none"
                  onClick={() => {
                    const existingIds = entryValues
                      .split(/[\n,]/)
                      .map((id) => id.trim())
                      .filter(Boolean);
                    if (!existingIds.includes(category.id)) {
                      setEntryValues([...existingIds, category.id].join("\n"));
                      onDirtyChange(true);
                    }
                    setSelectedCategory(category);
                    setEntryError(null);
                  }}
                >
                  <span className="font-medium text-ink">{category.name}</span>
                  <span className="text-xs text-muted-foreground">{category.path}</span>
                </button>
              ))}
              {categorySearch.hasMore ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={categorySearch.loading}
                  data-target-category-load-more
                  onClick={categorySearch.loadMore}
                >
                  {t("strategy.editor.categorySearchMore")}
                </Button>
              ) : null}
            </div>
          ) : null}
        </Field>
      ) : null}
      <Field invalid={Boolean(entryError)}>
        <FieldLabel htmlFor="smart-creation-enter-target-values">
          {t("strategy.editor.values")}
        </FieldLabel>
        <InputGroup>
          <Textarea
            unstyled
            ref={entryValuesRef}
            id="smart-creation-enter-target-values"
            value={entryValues}
            aria-invalid={Boolean(entryError)}
            placeholder={t(
              entryType === "keyword"
                ? "strategy.editor.keywordPlaceholder"
                : entryType === "product"
                  ? "strategy.editor.asinPlaceholder"
                  : "strategy.editor.categoryIdPlaceholder",
            )}
            onChange={(event) => {
              const nextValues = event.currentTarget.value;
              onDirtyChange(
                stagedOperationCount > 0 ||
                  nextValues.trim().length > 0 ||
                  entryMatchType !== initialEntryMatchType ||
                  entryBid !== initialEntryBid,
              );
              setEntryValues(nextValues);
              setSelectedCategory(null);
              setEntryError(null);
            }}
          />
        </InputGroup>
        {entryType === "category" ? (
          <FieldDescription>{t("strategy.editor.categoryIdHint")}</FieldDescription>
        ) : null}
        {entryType === "product" && branchKind === "branded" ? (
          <FieldDescription>{t("strategy.editor.ownAsinHint")}</FieldDescription>
        ) : null}
        {entryError ? <FieldError match>{entryError}</FieldError> : null}
      </Field>
      {entryType === "keyword" ? (
        <Field>
          <FieldLabel htmlFor="smart-creation-enter-target-match">
            {t("strategy.editor.matchTypeLabel")}
          </FieldLabel>
          <Select
            disabled={mutationPending}
            items={allowedMatchTypes(branchKind).map((value) => ({
              value,
              label: value,
            }))}
            value={entryMatchType}
            onValueChange={(value) => {
              if (!value) return;
              const nextMatchType = value as EntryMatchType;
              onDirtyChange(
                stagedOperationCount > 0 ||
                  entryValues.trim().length > 0 ||
                  nextMatchType !== initialEntryMatchType ||
                  entryBid !== initialEntryBid,
              );
              setEntryMatchType(nextMatchType);
            }}
          >
            <SelectTrigger id="smart-creation-enter-target-match" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {allowedMatchTypes(branchKind).map((value) => (
                  <SelectItem key={value} value={value} className="capitalize">
                    {value}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </Field>
      ) : null}
      <Field>
        <FieldLabel htmlFor="smart-creation-enter-target-bid">
          {t("strategy.editor.defaultBid")}
        </FieldLabel>
        <InputGroup>
          <InputGroupAddon>
            <InputGroupText>{capabilities.currencyCode}</InputGroupText>
          </InputGroupAddon>
          <InputGroupInput
            id="smart-creation-enter-target-bid"
            type="number"
            min={capabilities.bid.min}
            max={capabilities.bid.max}
            step={1 / 10 ** capabilities.bid.decimals}
            value={entryBid}
            onChange={(event) => {
              const nextBid = event.currentTarget.value;
              onDirtyChange(
                stagedOperationCount > 0 ||
                  entryValues.trim().length > 0 ||
                  entryMatchType !== initialEntryMatchType ||
                  nextBid !== initialEntryBid,
              );
              setEntryBid(nextBid);
              setEntryError(null);
            }}
          />
        </InputGroup>
        <FieldDescription>
          {capabilities.bid.min.toFixed(capabilities.bid.decimals)}–
          {capabilities.bid.max.toFixed(capabilities.bid.decimals)} {capabilities.currencyCode}
        </FieldDescription>
      </Field>
      <Button type="button" onClick={onSubmit}>
        <IconPlus data-icon="inline-start" />
        {t("strategy.editor.addToSelection")}
      </Button>
    </FieldGroup>
  );
}
